import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FileDropzone } from "@/components/FileDropzone";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader2, Landmark, Sparkles, ShieldAlert, KeyRound, Eye, EyeOff } from "lucide-react";

export default function Analyzer() {
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [files, setFiles] = useState([]);
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const hasPdf = files.some((f) => /\.pdf$/i.test(f.name));

  const start = async () => {
    if (!files.length) return toast.error("Add at least one statement file");
    setBusy(true);
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f));
    if (password.trim()) fd.append("password", password.trim());
    try {
      const { data } = await api.post("/extract/bank-statement", fd);
      toast.success("Extraction started");
      refresh();
      nav(`/workspace/${data.job_id}`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const demo = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/extract/sample");
      toast.success("Demo statement loaded");
      nav(`/workspace/${data.job_id}`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-5xl space-y-8" data-testid="analyzer-page">
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Mode B</p>
        <h1 className="mt-2 flex items-center gap-3 font-display text-3xl font-extrabold tracking-tighter">
          <Landmark className="h-7 w-7 text-primary" /> Bank Statement Analyzer
        </h1>
        <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
          Upload one or more statements (PDF, scanned PDF or photos). The bank format is detected
          automatically; if it cannot be identified confidently a generic parser is used.
        </p>
      </div>

      <FileDropzone files={files} setFiles={setFiles} />

      <div className="max-w-md space-y-2 rounded-lg border border-border bg-card p-4" data-testid="pdf-password-section">
        <Label htmlFor="pdf-password" className="flex items-center gap-2 text-sm font-semibold">
          <KeyRound className="h-4 w-4 text-primary" /> PDF password <span className="font-normal text-muted-foreground">(optional)</span>
        </Label>
        <div className="relative">
          <Input id="pdf-password" type={showPw ? "text" : "password"} value={password}
                 onChange={(e) => setPassword(e.target.value)} autoComplete="off"
                 placeholder="Leave blank if the PDF is not protected" className="pr-10"
                 data-testid="pdf-password-input" />
          <button type="button" onClick={() => setShowPw((s) => !s)} aria-label={showPw ? "Hide password" : "Show password"}
                  className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground hover:text-foreground"
                  data-testid="pdf-password-toggle">
            {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>
        <p className="text-xs text-muted-foreground" data-testid="pdf-password-hint">
          {hasPdf
            ? "Used to unlock password-protected PDFs in this batch before processing. It is never stored."
            : "Only needed when uploading a password-protected PDF statement."}
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Button onClick={start} disabled={busy} className="rounded-full" data-testid="start-extraction-btn">
          {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Extract &amp; Analyze
        </Button>
        <Button onClick={demo} disabled={busy} variant="outline" className="rounded-full bg-white"
                data-testid="load-demo-btn">
          <Sparkles className="mr-2 h-4 w-4" /> Load demo statement
        </Button>
        <span className="text-xs text-muted-foreground">Demo data is clearly labelled and uses no OCR credits.</span>
      </div>

      <div className="flex gap-3 rounded-lg border border-warn-border bg-warn p-4 text-sm text-warn-foreground">
        <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />
        <p>
          Analytical estimates are generated from extracted bank-statement data and may contain OCR or
          classification errors. Verify important information against the original statement. Any financial
          or credit-related assessment should be independently reviewed.
        </p>
      </div>
    </div>
  );
}
